/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button_Cancel,
  Button_Filter,
  Button_Filter_Active,
  Button_Menu,
  Button_Search,
  Button_Search_Active,
  Icon_Search_Small,
} from "@assets/images";
import "./Evidence.css";
import "../../App.css";
import EvidenceContainer from "@components/evidence/EvidenceContainer";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { IEvidence } from "@interfaces/Interfaces";
import { evidenceAtom } from "@state/State";
import { differenceInDays, format } from "date-fns";
import _ from "lodash";
import { EventRegister } from "react-native-event-listeners";
import { useHistory, withRouter } from "react-router";
import * as FirebaseService from "@controllers/FirebaseService";
import DataController from "@controllers/DataController";
import { Keyboard, KeyboardInfo } from "@capacitor/keyboard";
import { Capacitor } from "@capacitor/core";
import { ExportButton, ExportButtonContainer, HeaderTitle } from "parafolio-components";
import EvidenceSearchCard from "@components/evidence/EvidenceSearchCard";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import BlankEvidenceView from "@components/evidence/BlankEvidenceView";
import EvidenceFilterModal from "@components/evidence/EvidenceFilterModal";
import { evidenceFiltersAtom, EvidenceSearchFilter, filteredEvidenceSelector } from "@state/FilterState";
import EvidenceDownloadModal from "@components/evidence/EvidenceDownloadModal";
import EvidenceGapHeader from "@components/evidence/EvidenceGapHeader";
import EvidenceListHeader from "@components/evidence/EvidenceListHeader";

const Evidence: React.FC = () => {
  const history = useHistory();

  const searchRef = useRef<HTMLInputElement>(null);

  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [downloadVisible, setDownloadVisible] = useState<boolean>(false);

  const [searchVisible, setSearchVisible] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(format(new Date(), "MMM yyyy"));

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const evidenceFilters = useRecoilValue<EvidenceSearchFilter[]>(evidenceFiltersAtom);

  const filteredEvidence = useRecoilValueLoadable<IEvidence[]>(filteredEvidenceSelector);
  const filteredEvidenceWithSearch = useMemo(
    () => EvidenceUtils.searchEvidence(searchText, filteredEvidence.contents),
    [searchText, filteredEvidence.contents]
  );

  const activityTypesCount = useMemo(
    () => EvidenceUtils.getActivityTypesCountFromEvidence(filteredEvidenceWithSearch),
    [filteredEvidenceWithSearch]
  );
  const addedToAuditCount = useMemo(
    () => EvidenceUtils.getAddedToAuditCountFromEvidence(filteredEvidenceWithSearch),
    [filteredEvidenceWithSearch]
  );

  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        setKeyboardActive(true);
        setKeyboardHeight(info.keyboardHeight);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }
  }, []);

  useIonViewDidEnter(() => {
    EventRegister.emit("tab-bar/visibility-changed", true);
  });

  useIonViewDidLeave(() => {
    closeSearch();
  });

  useEffect(() => {
    const setPageName = async (): Promise<void> => {
      await FirebaseService.setScreenName("evidence");
    };

    setPageName();
  }, []);

  const closeSearch = useCallback((): void => {
    setSearchText("");
    setSearchVisible(false);
  }, []);

  useEffect(() => {
    if (searchVisible) {
      let searchPlaceholders: string[] = [];

      searchPlaceholders = [
        format(new Date(), "MMM yyyy"),
        ...filteredEvidenceWithSearch.map((item) => item.title || ""),
        ...filteredEvidenceWithSearch.map((item) => item.activity?.type || ""),
      ];

      searchPlaceholders = searchPlaceholders.filter((item) => item !== "");

      setSearchPlaceholder(searchPlaceholders[Math.floor(Math.random() * searchPlaceholders.length)]);

      try {
        setTimeout(() => {
          searchRef.current?.focus();
        }, 250);
      } catch (error) {
        console.log(error);
      }
    } else {
      closeSearch();
    }
  }, [closeSearch, filteredEvidenceWithSearch.length, searchVisible]);

  const toggleSearch = (): void => {
    setSearchVisible((prevState) => {
      const value = !prevState;

      if (value) {
        FirebaseService.logEvent("evidence_search_pressed", {});
      }

      return value;
    });
  };

  const addEvidence = async (): Promise<void> => {
    await FirebaseService.logEvent("add_evidence_pressed", {
      tab: "evidence",
      type: "empty_evidence_button",
    });

    EventRegister.emit("evidence/empty-add-clicked");
  };

  const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchText(event.target.value);
  };

  const searchFieldButtonClicked = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      closeSearch();
    }
  };

  const clearSearch = (focus?: boolean): void => {
    setSearchText("");
    focus && searchRef.current?.focus();
  };

  const evidenceCardClicked = (item: IEvidence): void => {
    const evidenceJSON = item.evidenceJSON;

    if (evidenceJSON && ProgramUtils.isWellbeingActivity(evidenceJSON.programInfo.progressCheckCompetenceID)) {
      history.push(
        `/dashboard/program/${evidenceJSON.programInfo.programID}/progress-check/${evidenceJSON.programInfo.progressCheckID}/wellbeing-activity/${evidenceJSON.programInfo.progressCheckCompetenceID}/results/${item.id}`
      );
    } else {
      const _evidence = _.pickBy(structuredClone(item), _.identity);

      history.push(`/evidence/read/${_evidence.id}`);
    }
  };

  const closeFilterModal = async (): Promise<void> => {
    try {
      // if (selectedStandards.length > 0 || selectedCustomTags.length > 0) {
      //   await FirebaseService.logEvent("filtered_evidence", {
      //     ...(selectedStandards.length > 0 && {
      //       selected_standards: JSON.stringify(selectedStandards),
      //     }),
      //     ...(selectedCustomTags.length > 0 && {
      //       selected_custom: selectedCustomTags.length,
      //     }),
      //     ...(selectedPrograms.length > 0 && {
      //       selected_programs: JSON.stringify(selectedPrograms),
      //     }),
      //   });
      // }
    } catch (error) {
      console.log(error);
    }

    setFilterVisible(false);
  };

  const closeDownloadModal = (): void => {
    setDownloadVisible(false);
  };

  const hasEvidenceGap = (): boolean => {
    if (!filteredEvidenceWithSearch || filteredEvidenceWithSearch.length === 0) {
      return false;
    }

    const dates = filteredEvidenceWithSearch.map((item) => new Date(item.date));

    if (dates.length > 1) {
      dates.sort((a, b) => a.getTime() - b.getTime());

      for (let i = 1; i < dates.length; i++) {
        const prevDate = dates[i - 1];
        const currDate = dates[i];

        const diffInDays = differenceInDays(currDate, prevDate);

        if (diffInDays > 90) {
          return true;
        }
      }
    }

    return false;
  };

  const hasLackOfType = (): boolean => {
    if (!filteredEvidenceWithSearch || filteredEvidenceWithSearch.length === 0) {
      return false;
    }

    // const types = filteredEvidence1.map((item) => )
    const activityTypes = filteredEvidenceWithSearch
      .filter((item) => item.activity?.type !== undefined)
      .map((item) => item.activity!.type);

    return Array.from(new Set(activityTypes)).length < 2;
  };

  const hasActiveFilter = useMemo(() => evidenceFilters.length > 0, [evidenceFilters]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton>
              <img
                alt="Menu"
                title="Menu"
                src={Button_Menu}
                className="menuIcon"
              />
            </IonMenuButton>
          </IonButtons>
          <HeaderTitle>{"Evidence"}</HeaderTitle>
          {EvidenceUtils.hasData(evidence.contents) && (
            <IonButtons slot="end">
              <IonButton
                id="evidenceFilterButton"
                className="header-button"
                mode="ios"
                onClick={() => setFilterVisible(true)}
              >
                <img
                  alt={hasActiveFilter ? "Filter Active" : "Filter"}
                  title={hasActiveFilter ? "Filter Active" : "Filter"}
                  src={hasActiveFilter ? Button_Filter_Active : Button_Filter}
                  className="headerIcon"
                />
              </IonButton>
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => toggleSearch()}
              >
                <img
                  alt={searchVisible ? "Search Active" : "Search"}
                  title={searchVisible ? "Search Active" : "Search"}
                  src={searchVisible ? Button_Search_Active : Button_Search}
                  className="headerIcon"
                />
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
        <EvidenceDownloadModal
          visible={downloadVisible}
          closeModal={() => closeDownloadModal()}
          evidence={filteredEvidenceWithSearch}
        />
        <EvidenceFilterModal
          visible={filterVisible}
          closeModal={closeFilterModal}
        />
        {EvidenceUtils.hasData(evidence.contents) && searchVisible && (
          <div className="evidenceSearchHeader">
            <div className="evidenceSearchInputContainer">
              <img
                alt=""
                src={Icon_Search_Small}
                className="evidenceSearchIcon"
              />
              <input
                value={searchText}
                className="searchInputField"
                placeholder={`Try searching “${searchPlaceholder}”`}
                onChange={searchTextChanged}
                ref={searchRef}
                onKeyDown={searchFieldButtonClicked}
              />
              {searchText.length > 0 && (
                <IonButton
                  className="searchClearButton"
                  mode="ios"
                  onClick={() => clearSearch(true)}
                >
                  <img
                    alt=""
                    src={Button_Cancel}
                    className="icon-44 mt-[-5px]"
                  />
                </IonButton>
              )}
            </div>
            {searchText.length > 0 && (
              <div className="evidenceResultsText">{EvidenceUtils.getSearchResultText(filteredEvidenceWithSearch)}</div>
            )}
          </div>
        )}
      </IonHeader>
      <IonContent className="page-background">
        <EvidenceContainer
          id="evidenceContentContainer"
          style={{
            paddingTop: 0,
            paddingBottom: keyboardActive ? keyboardHeight : EvidenceUtils.hasData(evidence.contents) ? "96px" : "40px",
          }}
        >
          {evidence.state === "hasValue" && evidence.contents && evidence.contents?.length > 0 ? (
            <>
              <EvidenceListHeader
                evidenceCount={filteredEvidenceWithSearch?.length ?? 0}
                activityTypesCount={activityTypesCount}
                addedToAuditCount={addedToAuditCount}
              />
              {(hasEvidenceGap() || hasLackOfType()) && (
                <EvidenceGapHeader
                  showGapHeader={hasEvidenceGap()}
                  showLackOfTypesHeader={hasLackOfType()}
                />
              )}
              <ExportButtonContainer>
                <ExportButton
                  disabled={filteredEvidenceWithSearch?.length === 0}
                  onClick={() => {
                    closeDownloadModal();
                    setDownloadVisible(true);
                  }}
                >
                  {"Download"}
                </ExportButton>
              </ExportButtonContainer>
              {filteredEvidenceWithSearch.map((item, index) => {
                const showHeader =
                  index === 0 ||
                  new Date(filteredEvidenceWithSearch[index - 1].date).getMonth() !== new Date(item.date).getMonth();

                return (
                  <EvidenceSearchCard
                    key={item.id}
                    buttonPressed={() => evidenceCardClicked(item) as any}
                    evidence={item}
                    searchText={searchText}
                    showHeader={showHeader}
                  />
                );
              })}
            </>
          ) : (
            <BlankEvidenceView addEvidence={() => addEvidence()} />
          )}
        </EvidenceContainer>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(Evidence);
